import * as THREE from '@phdsoft/three';

// was: /**
//  * Tolerance (NDC depth in [0,1]) for the occludee-vs-buffer comparison.
//  * Every mesh is rasterized as an occluder and then tested as an occludee, so a mesh
//  * that is itself the front surface compares its float64-interpolated depth with its
//  * own depth stored in the Float32Array. When float32 rounds that value down, a strict
//  * `depth <= stored` makes the mesh "behind itself" and culls it. 1e-6 is well above
//  * float32 rounding near depth 1 (~6e-8) and only ~1 cm at 70 m with near = 0.5.
//  */
// export const OCCLUSION_DEPTH_EPSILON = 1e-6;
// — replaced by the strict occludee depth margin below: the section culler tests a section
// before it rasterizes it (and rasterizes pinned sections untested), so a mesh is never compared
// with its own depth, and the +1e-6 tie tolerance only kept T-joint edges "visible".

/**
 * Constant part of the occludee depth margin, in NDC depth units ([0, 1] buffer depth).
 * A covered pixel centre of an occludee is Visible only when it is in front of the stored depth
 * by more than the margin (strict); a tie is not visible. 1.5e-7 is about 2.5 float32 ulps just
 * below depth 1, so float32 rounding of projected / stored depths never reads as "in front".
 * In world units it grows with the square of the view depth z: Δz ≈ ndc · z² · (f − n) / (f · n)
 * for a perspective camera (app camera n 0.5, f 5000: ≈ 0.0075 mm at 5 m, 3 mm at 100 m, 2.7 cm at 300 m).
 */
export const OCCLUDEE_DEPTH_MARGIN_NDC = 1.5e-7;

/**
 * Relative part of the occludee depth margin: a fraction of the view depth z of the stored
 * surface, so the world margin grows linearly (Δz = relative · z: 2.5e-4 is 1.25 mm at 5 m,
 * 2.5 cm at 100 m, 7.5 cm at 300 m). Absorbs mm-level joint noise (curved hull plates, converter
 * rounding) where a T-joint plate's edge lies on the face-on plate in front of it. Converted to
 * NDC at the stored depth for perspective and orthographic cameras (see updateMarginCoefficients);
 * ignored for other cameras. Together with the NDC part, the app camera's margin is about
 * 1.3 mm at 5 m, 5.1 mm at 20 m, 1.3 cm at 50 m, 2.8 cm at 100 m and 10 cm at 300 m.
 * `setOccludeeDepthMargin(-1e-6, 0)` restores the old tie-visible rule, up to exact equality: the
 * triangle test becomes `depth < stored + 1e-6` (old: `depth <= stored + 1e-6`) and isOccludedOBB
 * `minDepth < stored + 1e-6` (old: `minDepth <= stored`, no epsilon).
 */
export const OCCLUDEE_DEPTH_MARGIN_RELATIVE = 2.5e-4;

/**
 * Result of testing one triangle of an occludee against the depth buffer; testMeshOcclusion
 * returns the same tri-state for a whole mesh.
 */
// was: enum TriangleTest { — exported for testMeshOcclusion's callers
export enum TriangleTest {
  /**
   * At least one covered pixel centre is in front of the stored depth by more than the occludee
   * depth margin (strict; a tie is not visible) or has nothing drawn (stored depth still the
   * cleared far value 1), or the triangle is visible against background: a coverage-mask
   * sub-sample inside the triangle was not covered by any rasterized occluder (bounding-box rule
   * with coverageSubSamples 1: the triangle's footprint touches a pixel where nothing was drawn).
   */
  // was: "in front of (or within OCCLUSION_DEPTH_EPSILON of) the stored depth"
  // was: "…, or the triangle's footprint touches a pixel where nothing was drawn (stored depth still the cleared far value 1)."
  Visible,
  /**
   * Every covered pixel centre is behind the stored depth and every sub-sample inside the
   * triangle is covered (coverageSubSamples 1: no touched pixel is empty).
   */
  // was: /** Every covered pixel centre is behind the stored depth and no touched pixel is empty. */
  Occluded,
  /**
   * The triangle covers no pixel centre (sub-pixel, edge-on or degenerate) and no uncovered
   * sub-sample (coverageSubSamples 1: touches no empty pixel; a degenerate triangle is always
   * NoCoverage) — nothing can be concluded.
   */
  // was: "… and touches no empty pixel — nothing can be concluded."
  NoCoverage,
}

type AnyAttribute = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;

/**
 * The 12 triangles (6 faces) of a box given as 8 corners in the order
 * index = (x is max ? 4 : 0) | (y is max ? 2 : 0) | (z is max ? 1 : 0). Winding is irrelevant:
 * the occludee test keeps both windings.
 */
const BOX_TRIANGLES = new Uint8Array([
  0, 1, 3, 0, 3, 2, // x = min
  4, 6, 7, 4, 7, 5, // x = max
  0, 4, 5, 0, 5, 1, // y = min
  2, 3, 7, 2, 7, 6, // y = max
  0, 2, 6, 0, 6, 4, // z = min
  1, 5, 7, 1, 7, 3, // z = max
]);

/** testMeshTriangle: the triangle is not tested (masked, behind the camera or outside the depth range). */
const SKIPPED_TRIANGLE = -1;

/**
 * Sub-samples per cell axis of the coverage mask (k): k × k sub-samples per depth-buffer cell.
 * 1 = no coverage mask (legacy rule).
 */
export type CoverageSubSamples = 1 | 2 | 4;

/** Work counters of a SoftwareDepthBuffer, accumulated until resetCounters() (instrumentation only). */
export interface DepthBufferCounters {
  /** Milliseconds in projectMesh (cache hits included). */
  projectTimeMs: number;
  /** Vertices projected on cache misses. */
  verticesProjected: number;
  /** projectMesh calls answered from the one-mesh projection cache. */
  projectionCacheHits: number;
  /** Occludee triangles passed to the per-pixel test (mesh triangles and isOccludedBox face triangles). */
  trianglesTested: number;
  /** Occluder triangles passed to the per-pixel rasterizer. */
  trianglesRasterized: number;
  /**
   * Coverage mask: occluder (triangle, cell) pairs where the corner test found the cell fully inside
   * the triangle and marked it FULL (always 0 with coverageSubSamples 1).
   */
  coverageFullCells: number;
  /**
   * Coverage mask: occluder (triangle, cell) pairs where the cell was partial (neither fully inside
   * nor fully outside) and its k × k sub-samples were evaluated (always 0 with coverageSubSamples 1).
   */
  coverageSubSampleCells: number;
}

/**
 * Typed array + stride to read the components of an attribute directly, or null when
 * getX() must be used (interleaved, normalized or half-float data, or fewer than `minItemSize` components).
 */
function directComponents(attr: AnyAttribute, minItemSize: number): { array: ArrayLike<number>; stride: number } | null {
  if ((attr as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute || attr.normalized ||
      (attr as { isFloat16BufferAttribute?: boolean }).isFloat16BufferAttribute || attr.itemSize < minItemSize) {
    return null;
  }
  return { array: (attr as THREE.BufferAttribute).array, stride: attr.itemSize };
}

/** Version of the data behind an attribute (bumped by needsUpdate). */
function attributeVersion(attr: AnyAttribute): number {
  const interleaved = attr as THREE.InterleavedBufferAttribute;
  return interleaved.isInterleavedBufferAttribute ? interleaved.data.version : (attr as THREE.BufferAttribute).version;
}

/**
 * Why the last Visible / "not occluded" answer of SoftwareDepthBuffer was given (diagnostics only;
 * see SoftwareDepthBuffer.lastVisibleReason).
 */
export enum VisibleReason {
  /** No Visible answer recorded since the caller last reset the field. */
  None,
  /** A covered pixel centre was in front of the stored depth by more than the occludee margin. */
  Depth,
  /**
   * Visible against background: a covered pixel centre had nothing drawn (stored depth still the
   * cleared value 1), or a sub-sample inside the triangle was not covered by any occluder
   * (coverageSubSamples 1: the test touched a pixel where nothing was drawn).
   */
  // was: /** The test touched a pixel where nothing was drawn (stored depth still the cleared value 1). */
  EmptyPixel,
  /** The mesh or box was not tested per pixel (no geometry, over the triangle limit, straddling, projection failed). */
  Untested,
}

export class SoftwareDepthBuffer {
  /**
   * Reason of the last Visible / "not occluded" return of testTriangleVisible, testMeshOcclusion or
   * isOccludedBox. The caller resets it to None before a test and reads it after a Visible answer.
   */
  lastVisibleReason: VisibleReason = VisibleReason.None;
  /** Sub-samples per cell axis of the coverage mask (k); 1 = no mask. Fixed at construction. */
  readonly coverageSubSamples: CoverageSubSamples;
  /** Mask value of a fully covered cell: (1 << k²) − 1 (0xF for k = 2, 0xFFFF for k = 4); 0 for k = 1. */
  readonly coverageFull: number;
  private data: Float32Array;
  /**
   * Coverage mask, one entry per cell: bit sy·k + sx set when sub-sample (sx, sy), at cell offset
   * ((sx + 0.5) / k, (sy + 0.5) / k), is inside some rasterized occluder triangle. Empty for k = 1.
   */
  private readonly coverage: Uint16Array;
  /** The k sub-sample offsets relative to the cell centre, (j + 0.5) / k − 0.5 (built once per k). */
  private readonly subOffsets: Float64Array;
  /**
   * Edge steps of the triangle being rasterized or tested (k > 1, see edgeSteps):
   * [ax0, ay0, ax1, ay1, ax2, ay2, t0, t1, t2]. Reused, never reallocated.
   */
  private readonly edge = new Float64Array(9);
  private width: number;
  private height: number;
  private viewProjectionMatrix: THREE.Matrix4;
  private near: number = 0.1;
  private far: number = 1000;
  /** Projection of the last updateViewProjection camera (selects the margin formula). */
  private projectionKind: 'perspective' | 'orthographic' | 'other' = 'other';
  /** Occludee depth margin, constant part (NDC); see OCCLUDEE_DEPTH_MARGIN_NDC. */
  private marginNdc = OCCLUDEE_DEPTH_MARGIN_NDC;
  /** Occludee depth margin, relative part (fraction of view depth); see OCCLUDEE_DEPTH_MARGIN_RELATIVE. */
  private marginRelative = OCCLUDEE_DEPTH_MARGIN_RELATIVE;
  /**
   * A covered occludee pixel centre is Visible iff `stored >= 1 || depth < stored * marginScale - marginBias`
   * (see updateMarginCoefficients).
   */
  private marginScale = 1;
  private marginBias = OCCLUDEE_DEPTH_MARGIN_NDC;

  // Reusable temporaries for rasterizeMesh to avoid per-frame allocations
  private readonly _mvp = new THREE.Matrix4();
  // was: private readonly _v4 = new THREE.Vector4(); — projectMesh multiplies by the MVP inline

  // Projected vertices of the last projected mesh, [screenX, screenY, depthNorm, clipW] per
  // vertex, grown on demand (was: a new Float32Array per mesh per call). The key below lets
  // rasterizeMesh reuse the projection isOccludedMesh just made for the same plate.
  private projected = new Float32Array(0);
  private readonly projectedMvp = new THREE.Matrix4();
  private projectedMesh: THREE.Mesh | null = null;
  private projectedAttr: AnyAttribute | null = null;
  private projectedVersion = -1;
  private projectedCount = -1;
  /** Some vertex of the cached projection is behind the camera (w <= 0) or the near plane (depth < 0). */
  private projectedStraddles = false;
  /** Fallback copy of index attributes that cannot be read directly (grown on demand). */
  private indexScratch = new Uint32Array(0);
  /** isOccludedBox scratch: [screenX, screenY, depthNorm] per box corner (never reallocated). */
  private readonly boxProjected = new Float64Array(24);
  /**
   * Screen-rect area (buffer pixels², clamped to the buffer) of the box last passed to
   * isOccludedBox / boxRectPixels; Infinity when a corner was behind the camera or outside the
   * depth range. Lets the culler reuse the coarse test's projection for the occluder size gate.
   */
  private lastBoxRect = Infinity;

  /** See lastBoxRect. */
  get lastBoxRectPixels(): number {
    return this.lastBoxRect;
  }

  /** Instrumentation, accumulated across calls until resetCounters() (one object, never reallocated). */
  readonly counters: DepthBufferCounters = {
    projectTimeMs: 0,
    verticesProjected: 0,
    projectionCacheHits: 0,
    trianglesTested: 0,
    trianglesRasterized: 0,
    coverageFullCells: 0,
    coverageSubSampleCells: 0
  };

  // was: constructor(resolution: 128 | 256 = 128) {
  constructor(resolution: 128 | 256 = 128, coverageSubSamples: CoverageSubSamples = 4) {
    this.width = resolution;
    this.height = resolution;
    this.data = new Float32Array(this.width * this.height);
    // Guard (the value may come untyped from setConfig): Number(k) must be 1, 2 or 4, otherwise 4,
    // the same coercion as OcclusionCuller.coverageSamples.
    // was: const k = coverageSubSamples;
    const kValue = Number(coverageSubSamples);
    const k: CoverageSubSamples = kValue === 1 || kValue === 2 || kValue === 4 ? kValue : 4;
    this.coverageSubSamples = k;
    this.coverageFull = k > 1 ? (1 << (k * k)) - 1 : 0;
    // k = 1: the mask is not maintained, so it is not allocated either.
    this.coverage = new Uint16Array(k > 1 ? this.width * this.height : 0);
    this.subOffsets = new Float64Array(k);
    for (let j = 0; j < k; j++) {
      this.subOffsets[j] = (j + 0.5) / k - 0.5;
    }
    this.viewProjectionMatrix = new THREE.Matrix4();
    this.updateMarginCoefficients();
    this.clear();
  }

  /**
   * Set the occludee depth margin: `ndc` in NDC depth units plus `relative` × view depth of the
   * stored surface (see OCCLUDEE_DEPTH_MARGIN_NDC / OCCLUDEE_DEPTH_MARGIN_RELATIVE). Takes effect
   * immediately, whether called before or after updateViewProjection. `(-1e-6, 0)` restores the
   * old tie-visible rule, up to exact equality: the triangle test becomes `depth < stored + 1e-6`
   * (old: `<=`) and isOccludedOBB `minDepth < stored + 1e-6` (old: `minDepth <= stored`, no epsilon).
   *
   * Inputs are guarded (values may come untyped from setConfig in the console): both are coerced
   * with Number(); a non-finite `ndc` (NaN, undefined, non-numeric string) falls back to
   * OCCLUDEE_DEPTH_MARGIN_NDC and a non-finite `relative` to OCCLUDEE_DEPTH_MARGIN_RELATIVE, since
   * a NaN limit would read every covered pixel as Occluded (false culls). `relative` is clamped
   * to [0, 0.5] (an orthographic relative >= 1 would make marginScale <= 0). A negative finite
   * `ndc` stays allowed (legacy A/B value).
   */
  setOccludeeDepthMargin(ndc: number, relative: number): void {
    // was: this.marginNdc = ndc;
    // was: this.marginRelative = relative;
    const ndcValue = Number(ndc);
    const relativeValue = Number(relative);
    this.marginNdc = Number.isFinite(ndcValue) ? ndcValue : OCCLUDEE_DEPTH_MARGIN_NDC;
    this.marginRelative = Number.isFinite(relativeValue)
      ? Math.min(0.5, Math.max(0, relativeValue))
      : OCCLUDEE_DEPTH_MARGIN_RELATIVE;
    this.updateMarginCoefficients();
  }

  /**
   * Fold the margin into `limit = stored * marginScale - marginBias`. In NDC depth d ∈ [0, 1] a
   * world margin `relative × viewDepth` is `relative × (k − d)` for a perspective camera
   * (k = f / (f − n)) and `relative × (d + n / (f − n))` for an orthographic one, so
   * `stored − (ndc + relative × …)` becomes:
   * - perspective: marginBias = ndc + relative·k, marginScale = 1 + relative;
   * - orthographic: marginBias = ndc + relative·n/(f − n), marginScale = 1 − relative;
   * - any other camera (or far <= near): marginBias = ndc, marginScale = 1.
   */
  private updateMarginCoefficients(): void {
    const ndc = this.marginNdc;
    const relative = this.marginRelative;
    const near = this.near;
    const far = this.far;
    if (far > near && this.projectionKind === 'perspective') {
      this.marginBias = ndc + relative * (far / (far - near));
      this.marginScale = 1 + relative;
    } else if (far > near && this.projectionKind === 'orthographic') {
      this.marginBias = ndc + relative * (near / (far - near));
      this.marginScale = 1 - relative;
    } else {
      this.marginBias = ndc;
      this.marginScale = 1;
    }
  }

  /** Width (= height) of the buffer in cells. */
  get resolution(): number {
    return this.width;
  }

  clear(): void {
    this.data.fill(1.0); // 1 = far plane
    this.coverage.fill(0);
  }

  /**
   * Forget the cached projection key so the buffer holds no reference to a mesh or
   * attribute of a dropped scene (culler clear). The scratch arrays are kept.
   */
  resetProjectionCache(): void {
    this.projectedMesh = null;
    this.projectedAttr = null;
    this.projectedVersion = -1;
    this.projectedCount = -1;
    this.projectedStraddles = false;
  }

  /** Zero the instrumentation counters (the culler does it once per cull). */
  resetCounters(): void {
    const c = this.counters;
    c.projectTimeMs = 0;
    c.verticesProjected = 0;
    c.projectionCacheHits = 0;
    c.trianglesTested = 0;
    c.trianglesRasterized = 0;
    c.coverageFullCells = 0;
    c.coverageSubSampleCells = 0;
  }

  updateViewProjection(camera: THREE.Camera): void {
    if (camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera) {
      this.near = camera.near;
      this.far = camera.far;
    }
    this.projectionKind = camera instanceof THREE.PerspectiveCamera ? 'perspective'
      : camera instanceof THREE.OrthographicCamera ? 'orthographic' : 'other';
    this.updateMarginCoefficients();
    this.viewProjectionMatrix.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );
  }

  getDepth(x: number, y: number): number {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return 1.0;
    }
    return this.data[y * this.width + x];
  }

  /**
   * Coverage mask of cell (x, y): bit sy·k + sx per covered sub-sample, coverageFull when the cell
   * is fully covered. 0 outside the buffer and always 0 with coverageSubSamples 1.
   */
  getCoverage(x: number, y: number): number {
    if (this.coverageSubSamples === 1 || x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return 0;
    }
    return this.coverage[y * this.width + x];
  }

  private setDepth(x: number, y: number, depth: number): void {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return;
    }
    const index = y * this.width + x;
    // Write minimum depth (closest to camera)
    if (depth < this.data[index]) {
      this.data[index] = depth;
    }
  }

  /**
   * Rasterize an occluder using world-space OBB corners.
   * @param worldCorners - 8 corners of the OBB in world space
   * Writes no coverage bits: with coverageSubSamples > 1 anything behind this fill stays Visible
   * (safe but useless). Do not FULL-fill the rect: it is larger than the box's silhouette and would
   * deny rescues over background.
   */
  rasterizeOccluderOBB(worldCorners: THREE.Vector3[]): void {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minDepth = Infinity;

    for (const corner of worldCorners) {
      // Use Vector4 for explicit w check (Vector3.applyMatrix4 does implicit
      // perspective divide, producing incorrect results for behind-camera points)
      const v = new THREE.Vector4(corner.x, corner.y, corner.z, 1);
      v.applyMatrix4(this.viewProjectionMatrix);

      // Skip if behind camera
      if (v.w <= 0) continue;

      // Manual perspective divide
      const ndcX = v.x / v.w;
      const ndcY = v.y / v.w;
      const ndcZ = v.z / v.w;

      // Skip if outside NDC depth range
      if (ndcZ < -1 || ndcZ > 1) continue;

      // Convert from NDC to screen coordinates
      const screenX = Math.floor((ndcX + 1) * 0.5 * this.width);
      const screenY = Math.floor((1 - ndcY) * 0.5 * this.height);
      const depth = (ndcZ + 1) * 0.5; // Convert from [-1,1] to [0,1]

      minX = Math.min(minX, screenX);
      maxX = Math.max(maxX, screenX);
      minY = Math.min(minY, screenY);
      maxY = Math.max(maxY, screenY);
      minDepth = Math.min(minDepth, depth);
    }

    // Clamp to buffer bounds
    minX = Math.max(0, Math.floor(minX));
    maxX = Math.min(this.width - 1, Math.ceil(maxX));
    minY = Math.max(0, Math.floor(minY));
    maxY = Math.min(this.height - 1, Math.ceil(maxY));

    // Conservative rasterization: fill the bounding rect with min depth
    if (minX <= maxX && minY <= maxY && minDepth < 1) {
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          this.setDepth(x, y, minDepth);
        }
      }
    }
  }

  /**
   * Rasterize a single triangle with per-pixel depth interpolation.
   * Uses barycentric coordinates for inside-test and depth interpolation.
   * Keeps both windings for conservative occluder coverage.
   *
   * @param x0,y0,z0 - Screen-space vertex 0 (x,y in pixels; z in [0,1])
   * @param x1,y1,z1 - Screen-space vertex 1
   * @param x2,y2,z2 - Screen-space vertex 2
   */
  private rasterizeTriangle(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number
  ): void {
    // Signed area × 2 in screen space (Y-down).
    // Front-facing (originally CCW in clip space) → CW after Y flip → negative.
    const signedArea2 = (x1 - x0) * (y2 - y0) - (x2 - x0) * (y1 - y0);
    if (signedArea2 === 0) return; // degenerate only

    // Screen-space bounding box, clamped to buffer
    const bminX = Math.max(0, Math.floor(Math.min(x0, x1, x2)));
    const bmaxX = Math.min(this.width - 1, Math.ceil(Math.max(x0, x1, x2)));
    const bminY = Math.max(0, Math.floor(Math.min(y0, y1, y2)));
    const bmaxY = Math.min(this.height - 1, Math.ceil(Math.max(y0, y1, y2)));
    if (bminX > bmaxX || bminY > bmaxY) return;

    const invArea = 1.0 / signedArea2;
    const data = this.data;
    const width = this.width;

    // Pre-compute edge equation coefficients for barycentric interpolation
    const dy12 = y1 - y2;
    const dx21 = x2 - x1;
    const dy20 = y2 - y0;
    const dx02 = x0 - x2;

    const k = this.coverageSubSamples;
    if (k === 1) {
      // No coverage mask: the depth-only loop, verbatim (early exits as soon as a λ is negative), so
      // the k = 1 A/B measures the mask's cost and not a loop restructure.
      for (let y = bminY; y <= bmaxY; y++) {
        const py = y + 0.5;
        const dpy2 = py - y2;
        for (let x = bminX; x <= bmaxX; x++) {
          const px = x + 0.5;
          const dpx2 = px - x2;

          // Barycentric weights
          const lambda0 = (dy12 * dpx2 + dx21 * dpy2) * invArea;
          if (lambda0 < 0) continue;
          const lambda1 = (dy20 * dpx2 + dx02 * dpy2) * invArea;
          if (lambda1 < 0) continue;
          const lambda2 = 1 - lambda0 - lambda1;
          if (lambda2 < 0) continue;

          const depth = lambda0 * z0 + lambda1 * z1 + lambda2 * z2;
          // was: this.setDepth(x, y, depth); — x/y are clamped to the buffer, so keep the minimum directly
          const cell = y * width + x;
          if (depth < data[cell]) {
            data[cell] = depth;
          }
        }
      }
      return;
    }

    // Coverage mask (k > 1): per-pixel steps of each λ_i in x and y, and t_i = (|ax_i| + |ay_i|) / 2,
    // the most λ_i changes from the cell centre to a cell corner (λ_i is linear in screen space).
    const coverage = this.coverage;
    const full = this.coverageFull;
    const edge = this.edgeSteps(dy12, dx21, dy20, dx02, invArea);
    const t0 = edge[6];
    const t1 = edge[7];
    const t2 = edge[8];
    let fullCells = 0;
    let subSampleCells = 0;

    for (let y = bminY; y <= bmaxY; y++) {
      const py = y + 0.5;
      const dpy2 = py - y2;
      for (let x = bminX; x <= bmaxX; x++) {
        const px = x + 0.5;
        const dpx2 = px - x2;

        // Barycentric weights
        const lambda0 = (dy12 * dpx2 + dx21 * dpy2) * invArea;
        // was: if (lambda0 < 0) continue;
        const lambda1 = (dy20 * dpx2 + dx02 * dpy2) * invArea;
        // was: if (lambda1 < 0) continue;
        const lambda2 = 1 - lambda0 - lambda1;
        // was: if (lambda2 < 0) continue;
        // (the early continues would skip the coverage work below for cells whose centre is outside
        // the triangle, yet such a cell can still be partially covered; the depth update keeps the
        // same rule: only covered centres, all λ_i >= 0, write depth)
        const cell = y * width + x; // hoisted from the depth block below, shared with the coverage work

        if (lambda0 >= 0 && lambda1 >= 0 && lambda2 >= 0) {
          const depth = lambda0 * z0 + lambda1 * z1 + lambda2 * z2;
          // was: this.setDepth(x, y, depth); — x/y are clamped to the buffer, so keep the minimum directly
          // was: const cell = y * width + x; — hoisted to the λ block above
          if (depth < data[cell]) {
            data[cell] = depth;
          }
        }

        let mask = coverage[cell];
        if (mask === full) continue; // already fully covered: no coverage work behind a filled plate
        // Corner test: the minimum of λ_i over the cell's four corners is λ_i(centre) − t_i.
        if (lambda0 >= t0 && lambda1 >= t1 && lambda2 >= t2) {
          coverage[cell] = full;
          fullCells++;
          continue;
        }
        // Entirely outside one edge: the maximum over the corners, λ_i(centre) + t_i, is negative.
        if (lambda0 < -t0 || lambda1 < -t1 || lambda2 < -t2) continue;
        // Partial cell: evaluate the k × k sub-sample centres, skipping bits already set.
        mask |= this.subSamplesInside(lambda0, lambda1, lambda2, mask);
        coverage[cell] = mask;
        subSampleCells++;
      }
    }

    this.counters.coverageFullCells += fullCells;
    this.counters.coverageSubSampleCells += subSampleCells;
  }

  /**
   * Coverage mask (k > 1): store the per-pixel steps of the triangle's barycentrics in `edge` and
   * return it — [ax0, ay0, ax1, ay1, ax2, ay2, t0, t1, t2], where λ_i(x + dx, y + dy) =
   * λ_i(x, y) + dx·ax_i + dy·ay_i and t_i = (|ax_i| + |ay_i|) / 2 is the most λ_i changes from a cell
   * centre to a cell corner. Arguments are the edge coefficients and 1 / signedArea2 of
   * rasterizeTriangle / testTriangleVisible. Shared by the occluder and occludee sides so both sample
   * with the same arithmetic. No allocation.
   */
  private edgeSteps(dy12: number, dx21: number, dy20: number, dx02: number, invArea: number): Float64Array {
    const edge = this.edge;
    const ax0 = dy12 * invArea;
    const ay0 = dx21 * invArea;
    const ax1 = dy20 * invArea;
    const ay1 = dx02 * invArea;
    const ax2 = -(ax0 + ax1);
    const ay2 = -(ay0 + ay1);
    edge[0] = ax0;
    edge[1] = ay0;
    edge[2] = ax1;
    edge[3] = ay1;
    edge[4] = ax2;
    edge[5] = ay2;
    edge[6] = (Math.abs(ax0) + Math.abs(ay0)) * 0.5;
    edge[7] = (Math.abs(ax1) + Math.abs(ay1)) * 0.5;
    edge[8] = (Math.abs(ax2) + Math.abs(ay2)) * 0.5;
    return edge;
  }

  /**
   * Coverage mask (k > 1): the bits (sy·k + sx) of the cell's k × k sub-samples that lie inside the
   * triangle whose edge steps edgeSteps last stored, leaving out the bits set in `skip` (they are not
   * evaluated). λ0..λ2 are the barycentrics at the cell centre; a sub-sample is inside when every
   * λ_i(sub) = λ_i(centre) + oy·ay_i + ox·ax_i is >= 0 (the edge counts as inside). The occluder
   * ORs the result into the mask (skip = the mask); the occludee is visible against background when
   * the result is non-zero (skip = the mask: only sub-samples no occluder covered). No allocation.
   */
  private subSamplesInside(lambda0: number, lambda1: number, lambda2: number, skip: number): number {
    const edge = this.edge;
    const ax0 = edge[0];
    const ay0 = edge[1];
    const ax1 = edge[2];
    const ay1 = edge[3];
    const ax2 = edge[4];
    const ay2 = edge[5];
    const offs = this.subOffsets;
    const k = this.coverageSubSamples;
    let bits = 0;
    let bit = 1;
    for (let sy = 0; sy < k; sy++) {
      const oy = offs[sy];
      const r0 = lambda0 + oy * ay0;
      const r1 = lambda1 + oy * ay1;
      const r2 = lambda2 + oy * ay2;
      for (let sx = 0; sx < k; sx++, bit <<= 1) {
        if ((skip & bit) !== 0) continue;
        const ox = offs[sx];
        if (r0 + ox * ax0 >= 0 && r1 + ox * ax1 >= 0 && r2 + ox * ax2 >= 0) {
          bits |= bit;
        }
      }
    }
    return bits;
  }

  /**
   * Project every vertex of `mesh` (local → clip → screen) into the shared scratch array, or
   * reuse the projection already there when it was made for the same mesh, position attribute
   * (same data version and count) and model-view-projection. Also records whether any vertex
   * is behind the camera or the near plane (projectedStraddles).
   */
  private projectMesh(mesh: THREE.Mesh, posAttr: AnyAttribute): Float32Array {
    const startTime = performance.now();
    // Combined model-view-projection matrix
    mesh.updateMatrixWorld();
    const mvp = this._mvp;
    mvp.multiplyMatrices(this.viewProjectionMatrix, mesh.matrixWorld);

    const vertexCount = posAttr.count;
    const version = attributeVersion(posAttr);
    if (this.projectedMesh === mesh && this.projectedAttr === posAttr && this.projectedVersion === version &&
        this.projectedCount === vertexCount && this.projectedMvp.equals(mvp)) {
      this.counters.projectionCacheHits++;
      this.counters.projectTimeMs += performance.now() - startTime;
      return this.projected;
    }

    if (this.projected.length < vertexCount * 4) {
      this.projected = new Float32Array(Math.max(vertexCount * 4, this.projected.length * 2));
    }
    // Layout: [screenX, screenY, depthNorm, clipW] per vertex
    const projected = this.projected;
    const e = mvp.elements;
    const width = this.width;
    const height = this.height;
    const direct = directComponents(posAttr, 3);
    const positions = direct ? direct.array : null;
    const stride = direct ? direct.stride : 0;
    let straddles = false;

    for (let i = 0; i < vertexCount; i++) {
      let x: number, y: number, z: number;
      if (positions) {
        const p = i * stride;
        x = positions[p];
        y = positions[p + 1];
        z = positions[p + 2];
      } else {
        x = posAttr.getX(i);
        y = posAttr.getY(i);
        z = posAttr.getZ(i);
      }
      // Same arithmetic as Vector4(x, y, z, 1).applyMatrix4(mvp).
      const w = e[3] * x + e[7] * y + e[11] * z + e[15];
      const o = i * 4;
      if (w <= 0) {
        // Behind camera — mark invalid
        projected[o] = 0;
        projected[o + 1] = 0;
        projected[o + 2] = 0;
        projected[o + 3] = 0;
        straddles = true;
        continue;
      }

      // Perspective divide → NDC → screen
      const ndcX = (e[0] * x + e[4] * y + e[8] * z + e[12]) / w;
      const ndcY = (e[1] * x + e[5] * y + e[9] * z + e[13]) / w;
      const ndcZ = (e[2] * x + e[6] * y + e[10] * z + e[14]) / w;

      projected[o] = (ndcX + 1) * 0.5 * width;
      projected[o + 1] = (1 - ndcY) * 0.5 * height;
      projected[o + 2] = (ndcZ + 1) * 0.5; // map [-1,1] → [0,1]
      projected[o + 3] = w;
      if (projected[o + 3] <= 0 || projected[o + 2] < 0) {
        straddles = true; // behind the near plane (read back as float32, like the old check)
      }
    }

    this.projectedMesh = mesh;
    this.projectedAttr = posAttr;
    this.projectedVersion = version;
    this.projectedCount = vertexCount;
    this.projectedMvp.copy(mvp);
    this.projectedStraddles = straddles;
    this.counters.verticesProjected += vertexCount;
    this.counters.projectTimeMs += performance.now() - startTime;
    return projected;
  }

  /** The index array (read directly when possible, else copied via getX), or null when non-indexed. */
  private indexArrayOf(index: THREE.BufferAttribute | null): ArrayLike<number> | null {
    if (!index) {
      return null;
    }
    const direct = directComponents(index, 1);
    if (direct && direct.stride === 1) {
      return direct.array;
    }
    if (this.indexScratch.length < index.count) {
      this.indexScratch = new Uint32Array(index.count);
    }
    for (let i = 0; i < index.count; i++) {
      this.indexScratch[i] = index.getX(i);
    }
    return this.indexScratch;
  }

  /**
   * Rasterize a mesh's actual geometry triangles into the depth buffer.
   * Transforms each vertex local → clip space, performs perspective divide,
   * and rasterizes each front-facing triangle with interpolated depth.
   *
   * @param mesh - The mesh to rasterize
   * @param maxTriangles - Skip meshes with more triangles than this (returns false)
   * @returns true if rasterized, false if skipped (too many triangles or no geometry)
   */
  rasterizeMesh(mesh: THREE.Mesh, maxTriangles: number = 10000): boolean {
    const geometry = mesh.geometry;
    if (!geometry) return false;

    const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr) return false;

    const index = geometry.getIndex();
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    if (triCount > maxTriangles || triCount === 0) return false;

    // Lightsection mask: triangles whose _EL_IDX is set in __lsMaskData are
    // visually discarded by the shader (a "hole" in the merged plate). Skip them
    // so the depth buffer reflects the real silhouette — otherwise things behind
    // the hole would get false-occluded.
    const maskData = mesh.userData.__lsMaskData as Uint8Array | undefined;
    const elIdxAttr = mesh.userData.__lsElIdxAttr as THREE.BufferAttribute | undefined;
    const maskActive = maskData !== undefined && elIdxAttr !== undefined;

    // Project all vertices to screen space once (reuses isOccludedMesh's projection of the same plate).
    // was: mesh.updateMatrixWorld(); mvp = VP × matrixWorld; projected = new Float32Array(vertexCount * 4);
    //      v.set(getX, getY, getZ, 1).applyMatrix4(mvp) per vertex — now projectMesh (typed arrays, shared scratch)
    const projected = this.projectMesh(mesh, posAttr);
    const indexArray = this.indexArrayOf(index);
    const elIdx = maskActive ? directComponents(elIdxAttr!, 1) : null;
    let rasterized = 0;

    // Rasterize each triangle
    for (let t = 0; t < triCount; t++) {
      let i0: number, i1: number, i2: number;
      // was: if (index) { i0 = index.getX(t * 3); ... }
      if (indexArray) {
        i0 = indexArray[t * 3];
        i1 = indexArray[t * 3 + 1];
        i2 = indexArray[t * 3 + 2];
      } else {
        i0 = t * 3;
        i1 = t * 3 + 1;
        i2 = t * 3 + 2;
      }

      // Skip masked triangles (their fragments are discarded by the shader).
      // stampElementIndex fills all vertices of an element with the same _EL_IDX,
      // so sampling any one vertex is sufficient.
      if (maskActive) {
        // was: const elIdx = elIdxAttr!.getX(i0);
        const elementIndex = elIdx ? elIdx.array[i0 * elIdx.stride] : elIdxAttr!.getX(i0);
        if (elementIndex >= 0 && elementIndex < maskData!.length && maskData![elementIndex] !== 0) {
          continue;
        }
      }

      // Skip triangles with any vertex behind camera
      if (projected[i0 * 4 + 3] <= 0 ||
          projected[i1 * 4 + 3] <= 0 ||
          projected[i2 * 4 + 3] <= 0) {
        continue;
      }

      const z0 = projected[i0 * 4 + 2];
      const z1 = projected[i1 * 4 + 2];
      const z2 = projected[i2 * 4 + 2];

      // Skip triangles entirely outside NDC depth range
      if ((z0 < 0 && z1 < 0 && z2 < 0) || (z0 > 1 && z1 > 1 && z2 > 1)) {
        continue;
      }

      this.rasterizeTriangle(
        projected[i0 * 4], projected[i0 * 4 + 1], z0,
        projected[i1 * 4], projected[i1 * 4 + 1], z1,
        projected[i2 * 4], projected[i2 * 4 + 1], z2
      );
      rasterized++;
    }

    this.counters.trianglesRasterized += rasterized;
    return true;
  }

  /**
   * Test a single triangle against the depth buffer.
   * Tests BOTH front-facing and back-facing triangles (conservative: materials
   * may use DoubleSide, and winding order may be inconsistent in imported models).
   *
   * Visible when either:
   * - a covered pixel centre is in front of the stored depth by more than the occludee depth
   *   margin (strict; a tie is not visible — `depth < stored * marginScale - marginBias`;
   *   was: "+ OCCLUSION_DEPTH_EPSILON, so a mesh is never culled by its own float32-rounded depth"),
   *   or a covered pixel centre's stored depth is still the cleared far value 1, or
   * - visible against background: a coverage-mask sub-sample inside the triangle was not covered
   *   by any rasterized occluder (bounding-box rule with coverageSubSamples 1: the triangle's
   *   screen bounding box touches a pixel whose stored depth is still the cleared far value).
   *   The sub-sample check runs on every cell of the bounding box that is not FULL and not
   *   entirely outside the triangle, whether or not the cell centre is covered — so a corner the
   *   occluder leaves empty next to a covered centre also counts. Occluders and occludees are
   *   sampled with the same rule (edgeSteps / subSamplesInside), so, up to rounding on a shared
   *   edge, a plate whose projection lies inside a nearer plate's projection (a deck ending on the
   *   hull) finds no uncovered sub-sample.
   *   (was: "the triangle's screen bounding box touches a pixel whose stored depth is still the
   *   cleared far value (nothing was drawn there). Thin / edge-on meshes may cover only a few
   *   pixel centres, all behind another surface, while their real footprint reaches past that
   *   surface's outline into empty buffer.")
   * A triangle that covers no pixel centre and no uncovered sub-sample reports NoCoverage rather
   * than Occluded: only a covered pixel that is behind the buffer is evidence of occlusion. A
   * degenerate (zero-area) triangle is NoCoverage for every k (was: Visible when its bounding box
   * touched an empty pixel); an exactly edge-on plate stays visible through the no-evidence path.
   * Accepted residual (k > 1): a strip of the triangle thinner than one sub-sample past the
   * occluders' outline is not seen. Also, coverage bits carry no depth: an occludee strip in front
   * of a farther, earlier-tested occluder that covers no cell centre is hidden (up to ~1 cell ≈ 14
   * GPU px at 1920 wide); likewise behind occluder triangles partly beyond the far plane (rasterised,
   * bits set where the GPU clips). Measured by the GPU false-cull sweep; a per-cell farthest-coverage
   * depth would remove it if needed.
   */
  private testTriangleVisible(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number
  ): TriangleTest {
    const signedArea2 = (x1 - x0) * (y2 - y0) - (x2 - x0) * (y1 - y0);
    // was: if (signedArea2 === 0) return TriangleTest.NoCoverage; — zero-area triangles now run the empty-pixel rule below
    // (coverage mask: zero-area triangles are NoCoverage again, after the bounding-box check below)

    // Footprint: pixel x covers [x, x+1), so the last pixel the triangle reaches is
    // floor(max). (Math.ceil(max) could add a pixel the triangle never touches, which
    // would turn into a spurious "empty pixel" hit.) Every covered pixel centre
    // x+0.5 <= max also satisfies x <= floor(max), so this range is enough for centres.
    const bminX = Math.max(0, Math.floor(Math.min(x0, x1, x2)));
    // was: const bmaxX = Math.min(this.width - 1, Math.ceil(Math.max(x0, x1, x2))); — see footprint note
    const bmaxX = Math.min(this.width - 1, Math.floor(Math.max(x0, x1, x2)));
    const bminY = Math.max(0, Math.floor(Math.min(y0, y1, y2)));
    // was: const bmaxY = Math.min(this.height - 1, Math.ceil(Math.max(y0, y1, y2))); — see footprint note
    const bmaxY = Math.min(this.height - 1, Math.floor(Math.max(y0, y1, y2)));
    if (bminX > bmaxX || bminY > bmaxY) return TriangleTest.NoCoverage;

    if (signedArea2 === 0) {
      // Degenerate (edge-on) triangle: no barycentrics, so no covered centre and no sub-sample
      // inside it — no evidence either way, for every k (the section is kept as NoCoverage).
      // was: (only the empty-pixel rule)
      // was: for (let y = bminY; y <= bmaxY; y++) {
      // was:   for (let x = bminX; x <= bmaxX; x++) {
      // was:     if (this.getDepth(x, y) >= 1) {
      // was:       this.lastVisibleReason = VisibleReason.EmptyPixel;
      // was:       return TriangleTest.Visible;
      // was:     }
      // was:   }
      // was: }
      return TriangleTest.NoCoverage;
    }

    const invArea = 1.0 / signedArea2;
    const data = this.data;
    const width = this.width;
    const dy12 = y1 - y2;
    const dx21 = x2 - x1;
    const dy20 = y2 - y0;
    const dx02 = x0 - x2;

    // Coverage mask (k > 1): the occluder side's edge steps and corner thresholds (edgeSteps).
    const k = this.coverageSubSamples;
    const coverage = this.coverage;
    const full = this.coverageFull;
    let t0 = 0, t1 = 0, t2 = 0;
    if (k > 1) {
      const edge = this.edgeSteps(dy12, dx21, dy20, dx02, invArea);
      t0 = edge[6];
      t1 = edge[7];
      t2 = edge[8];
    }

    const marginScale = this.marginScale;
    const marginBias = this.marginBias;
    let covered = false;
    for (let y = bminY; y <= bmaxY; y++) {
      const py = y + 0.5;
      const dpy2 = py - y2;
      const row = y * width;
      for (let x = bminX; x <= bmaxX; x++) {
        const px = x + 0.5;
        const dpx2 = px - x2;
        // was: const stored = this.getDepth(x, y); — x/y are clamped to the buffer
        const stored = data[row + x];

        const lambda0 = (dy12 * dpx2 + dx21 * dpy2) * invArea;
        const lambda1 = (dy20 * dpx2 + dx02 * dpy2) * invArea;
        const lambda2 = 1 - lambda0 - lambda1;
        if (lambda0 < 0 || lambda1 < 0 || lambda2 < 0) {
          if (k === 1) {
            // Legacy rule (coverageSubSamples 1): centre not covered, but the footprint
            // touches this pixel: if nothing was drawn here the triangle shows against the background.
            if (stored >= 1) {
              this.lastVisibleReason = VisibleReason.EmptyPixel;
              return TriangleTest.Visible;
            }
            continue;
          }
          // was: if (stored >= 1) { this.lastVisibleReason = VisibleReason.EmptyPixel; return TriangleTest.Visible; }
          // was: continue;
          // — k > 1: the coverage check below (an uncovered sub-sample inside the triangle)
        } else {
          covered = true;
          const depth = lambda0 * z0 + lambda1 * z1 + lambda2 * z2;
          // This pixel of the tested mesh is in front of the depth buffer by more than the
          // occludee depth margin → visible (a tie is not). stored >= 1 is an empty pixel.
          // was: if (depth <= this.getDepth(x, y)) { — float32 self-tie, see OCCLUSION_DEPTH_EPSILON
          // was: if (depth <= stored + OCCLUSION_DEPTH_EPSILON) {
          // was: if (stored >= 1 || depth < stored * marginScale - marginBias) { return TriangleTest.Visible; } — split to record the reason
          if (stored >= 1) {
            this.lastVisibleReason = VisibleReason.EmptyPixel;
            return TriangleTest.Visible;
          }
          if (depth < stored * marginScale - marginBias) {
            this.lastVisibleReason = VisibleReason.Depth;
            return TriangleTest.Visible;
          }
          // Covered centre behind the buffer: occlusion evidence; with k > 1 the cell's corners
          // still go through the coverage check below (the hull may cover the centre but leave a
          // corner the triangle reaches empty).
        }

        // Coverage check (k > 1), centre covered or not: unless the cell is FULL or entirely
        // outside one edge (λ_i(centre) < −t_i), any sub-sample inside the triangle whose bit no
        // occluder set shows the triangle against the background.
        if (k > 1) {
          const mask = coverage[row + x];
          if (mask !== full && !(lambda0 < -t0 || lambda1 < -t1 || lambda2 < -t2) &&
              this.subSamplesInside(lambda0, lambda1, lambda2, mask) !== 0) {
            this.lastVisibleReason = VisibleReason.EmptyPixel;
            return TriangleTest.Visible;
          }
        }
      }
    }
    return covered ? TriangleTest.Occluded : TriangleTest.NoCoverage;
  }

  /**
   * Test if a mesh is fully occluded by comparing its actual triangle depths
   * per-pixel against the depth buffer.
   *
   * Returns true if fully occluded (no covered triangle pixel is in front of the depth
   * buffer by more than the occludee depth margin — strict, a tie is not visible — and the mesh
   * is nowhere visible against background: every sub-sample inside its triangles was covered by
   * some rasterized occluder, bounding-box rule with coverageSubSamples 1; see testTriangleVisible),
   * false if any pixel is potentially visible.
   * (was: "no triangle pixel is closer than the depth buffer + OCCLUSION_DEPTH_EPSILON")
   * (was: "… and no triangle touches a pixel where nothing was drawn")
   *
   * @param mesh - The mesh to test
   * @param maxTriangles - Skip meshes with more triangles than this (returns false = not occluded)
   * @returns true if fully occluded, false if potentially visible or skipped
   */
  isOccludedMesh(mesh: THREE.Mesh, maxTriangles: number = 10000): boolean {
    return this.testMeshOcclusion(mesh, maxTriangles) === TriangleTest.Occluded;
  }

  /**
   * isOccludedMesh as a tri-state: Occluded exactly when isOccludedMesh is true; NoCoverage
   * when no triangle was Visible and none covered a pixel centre; Visible otherwise (a
   * visible triangle, or the mesh was skipped: no geometry, over maxTriangles, straddling
   * the near plane).
   *
   * Strided early-accept (`earlyAcceptStride` k > 1): triangles 0, k, 2k, … are tested first and
   * the first Visible one returns; only then are the other triangles tested (each triangle at most
   * once). The buffer does not change during the call and every triangle is still tested before
   * Occluded / NoCoverage is returned, so the result is the same as with k = 1; only the order
   * (and so how soon a visible mesh stops) differs.
   */
  // was: testMeshOcclusion(mesh: THREE.Mesh, maxTriangles: number = 10000): TriangleTest {
  testMeshOcclusion(mesh: THREE.Mesh, maxTriangles: number = 10000, earlyAcceptStride: number = 1): TriangleTest {
    // was: the body of isOccludedMesh returning false / true — now Visible / Occluded / NoCoverage
    const geometry = mesh.geometry;
    if (!geometry) {
      this.lastVisibleReason = VisibleReason.Untested;
      return TriangleTest.Visible;
    }

    const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr) {
      this.lastVisibleReason = VisibleReason.Untested;
      return TriangleTest.Visible;
    }

    const index = geometry.getIndex();
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    if (triCount > maxTriangles || triCount === 0) {
      this.lastVisibleReason = VisibleReason.Untested;
      return TriangleTest.Visible;
    }

    // Mirror the mask handling from rasterizeMesh: masked triangles are not
    // drawn by the GPU, so they shouldn't count as "visible" when testing
    // occlusion. Only non-masked triangles can rescue a mesh from being culled.
    const maskData = mesh.userData.__lsMaskData as Uint8Array | undefined;
    const elIdxAttr = mesh.userData.__lsElIdxAttr as THREE.BufferAttribute | undefined;
    const maskActive = maskData !== undefined && elIdxAttr !== undefined;

    // was: mvp = VP × matrixWorld; projected = new Float32Array(vertexCount * 4); getX/applyMatrix4 per
    //      vertex, then a second pass for the straddle check — now projectMesh (typed arrays, shared scratch)
    const projected = this.projectMesh(mesh, posAttr);

    // If any vertex is behind the camera (w <= 0, perspective) or behind the
    // near plane (depth < 0, works for both perspective and orthographic),
    // the mesh straddles the view volume — treat as visible.
    if (this.projectedStraddles) {
      this.lastVisibleReason = VisibleReason.Untested;
      return TriangleTest.Visible;
    }

    const indexArray = this.indexArrayOf(index);
    const elIdx = maskActive ? directComponents(elIdxAttr!, 1) : null;

    // was: one index-order loop with the triangle test inlined — its body is now testMeshTriangle, run by the two passes below
    const stride = earlyAcceptStride >= 2 ? Math.floor(earlyAcceptStride) : 1; // NaN / < 2 → plain order
    let anyCovered = false;
    let tested = 0;
    if (stride > 1) {
      // Pass 1: every stride-th triangle; a visible mesh usually stops here.
      for (let t = 0; t < triCount; t += stride) {
        const result = this.testMeshTriangle(t, projected, indexArray, maskData, elIdxAttr, elIdx);
        if (result === SKIPPED_TRIANGLE) {
          continue;
        }
        tested++;
        if (result === TriangleTest.Visible) {
          this.counters.trianglesTested += tested;
          return TriangleTest.Visible;
        }
        if (result === TriangleTest.Occluded) {
          anyCovered = true;
        }
      }
    }
    // Pass 2 (the only pass with stride 1): the triangles pass 1 did not test, in index order.
    const first = stride > 1 ? 1 : 0;
    for (let base = 0; base < triCount; base += stride) {
      const end = Math.min(base + stride, triCount);
      for (let t = base + first; t < end; t++) {
        const result = this.testMeshTriangle(t, projected, indexArray, maskData, elIdxAttr, elIdx);
        if (result === SKIPPED_TRIANGLE) {
          continue;
        }
        tested++;
        if (result === TriangleTest.Visible) {
          this.counters.trianglesTested += tested;
          // was: return false;
          return TriangleTest.Visible; // At least one triangle pixel is visible
        }
        if (result === TriangleTest.Occluded) {
          anyCovered = true;
        }
      }
    }
    this.counters.trianglesTested += tested;

    // Only report occlusion when at least one covered pixel was actually behind
    // the buffer. A mesh too small or too edge-on to cover any pixel centre is
    // not evidence of occlusion — culling it would pop sections in and out.
    // was: return anyCovered;
    return anyCovered ? TriangleTest.Occluded : TriangleTest.NoCoverage;
  }

  /**
   * Test triangle `t` of the mesh whose vertices are in `projected` (testMeshOcclusion's loop
   * body): SKIPPED_TRIANGLE when it is masked (not drawn by the shader), has a vertex behind the
   * camera, or lies wholly outside the depth range; else testTriangleVisible's result.
   */
  private testMeshTriangle(
    t: number, projected: Float32Array, indexArray: ArrayLike<number> | null,
    maskData: Uint8Array | undefined, elIdxAttr: THREE.BufferAttribute | undefined,
    elIdx: { array: ArrayLike<number>; stride: number } | null
  ): number {
    let i0: number, i1: number, i2: number;
    // was: if (index) { i0 = index.getX(t * 3); ... }
    if (indexArray) {
      i0 = indexArray[t * 3];
      i1 = indexArray[t * 3 + 1];
      i2 = indexArray[t * 3 + 2];
    } else {
      i0 = t * 3;
      i1 = t * 3 + 1;
      i2 = t * 3 + 2;
    }

    if (maskData !== undefined && elIdxAttr !== undefined) {
      // was: const elIdx = elIdxAttr!.getX(i0);
      const elementIndex = elIdx ? elIdx.array[i0 * elIdx.stride] : elIdxAttr.getX(i0);
      if (elementIndex >= 0 && elementIndex < maskData.length && maskData[elementIndex] !== 0) {
        return SKIPPED_TRIANGLE;
      }
    }

    if (projected[i0 * 4 + 3] <= 0 ||
        projected[i1 * 4 + 3] <= 0 ||
        projected[i2 * 4 + 3] <= 0) {
      return SKIPPED_TRIANGLE;
    }

    const z0 = projected[i0 * 4 + 2];
    const z1 = projected[i1 * 4 + 2];
    const z2 = projected[i2 * 4 + 2];

    if ((z0 < 0 && z1 < 0 && z2 < 0) || (z0 > 1 && z1 > 1 && z2 > 1)) {
      return SKIPPED_TRIANGLE;
    }

    return this.testTriangleVisible(
      projected[i0 * 4], projected[i0 * 4 + 1], z0,
      projected[i1 * 4], projected[i1 * 4 + 1], z1,
      projected[i2 * 4], projected[i2 * 4 + 1], z2
    );
  }

  /**
   * Is a box (8 world corners, flat xyz, in BOX_TRIANGLES corner order) fully hidden? Its 12
   * face triangles go through the same per-pixel test as isOccludedMesh's triangles
   * (in front by more than the occludee depth margin, strict, a tie is not visible — was:
   * OCCLUSION_DEPTH_EPSILON; visible against background only where a sub-sample inside the face
   * triangle was not covered by any rasterized occluder, bounding-box empty-pixel rule with
   * coverageSubSamples 1 — was: "empty-pixel rule"; edge-on faces are degenerate → NoCoverage;
   * both windings): any Visible triangle → false;
   * at least one Occluded and none Visible → true; only NoCoverage → false (no evidence).
   * Any corner behind the camera, the near plane or the far plane → false. Unlike
   * isOccludedOBB (nearest corner depth over the whole screen rect), an oblique / elongated
   * box is compared face by face, and the front faces are nearer than everything inside the
   * box under the same pixel, so "box occluded" still implies "contents occluded".
   * No allocation per call.
   */
  isOccludedBox(corners: ArrayLike<number>): boolean {
    // was: the corner projection loop inline (return false on w <= 0 / ndcZ outside [-1, 1]) — now projectBox,
    // which also records lastBoxRectPixels
    if (!this.projectBox(corners)) {
      this.lastVisibleReason = VisibleReason.Untested;
      return false; // behind the camera or outside the depth range (near or far plane)
    }
    const p = this.boxProjected;

    let anyCovered = false;
    for (let t = 0; t < 36; t += 3) {
      const a = BOX_TRIANGLES[t] * 3;
      const b = BOX_TRIANGLES[t + 1] * 3;
      const c = BOX_TRIANGLES[t + 2] * 3;
      const result = this.testTriangleVisible(
        p[a], p[a + 1], p[a + 2],
        p[b], p[b + 1], p[b + 2],
        p[c], p[c + 1], p[c + 2]
      );
      if (result === TriangleTest.Visible) {
        this.counters.trianglesTested += t / 3 + 1;
        return false;
      }
      if (result === TriangleTest.Occluded) {
        anyCovered = true;
      }
    }
    this.counters.trianglesTested += 12;
    // Every face NoCoverage → false with lastVisibleReason left None (the culler counts it as untested).
    return anyCovered;
  }

  /**
   * Area (buffer pixels², continuous) of the screen rect of a box given as 8 world corners
   * (flat xyz, BOX_TRIANGLES order), clamped to the buffer; Infinity when a corner is behind the
   * camera or outside the depth range (a box crossing the near plane is close to the camera).
   * Same projection as isOccludedBox; no allocation.
   */
  boxRectPixels(corners: ArrayLike<number>): number {
    this.projectBox(corners);
    return this.lastBoxRect;
  }

  /**
   * Project 8 box corners into boxProjected and record their clamped screen-rect area in
   * lastBoxRectPixels. False (rect Infinity, boxProjected partly written) when a corner is
   * behind the camera (w <= 0) or outside the NDC depth range.
   */
  private projectBox(corners: ArrayLike<number>): boolean {
    const e = this.viewProjectionMatrix.elements;
    const p = this.boxProjected;
    const width = this.width;
    const height = this.height;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let i = 0; i < 8; i++) {
      const x = corners[i * 3];
      const y = corners[i * 3 + 1];
      const z = corners[i * 3 + 2];
      // Same arithmetic as projectMesh (Vector4(x, y, z, 1).applyMatrix4(VP)).
      const w = e[3] * x + e[7] * y + e[11] * z + e[15];
      if (w <= 0) {
        this.lastBoxRect = Infinity;
        return false; // behind the camera: the box straddles the view volume
      }
      const ndcZ = (e[2] * x + e[6] * y + e[10] * z + e[14]) / w;
      if (ndcZ < -1 || ndcZ > 1) {
        this.lastBoxRect = Infinity;
        return false; // outside the depth range (near or far plane)
      }
      const o = i * 3;
      const sx = ((e[0] * x + e[4] * y + e[8] * z + e[12]) / w + 1) * 0.5 * width;
      const sy = (1 - (e[1] * x + e[5] * y + e[9] * z + e[13]) / w) * 0.5 * height;
      p[o] = sx;
      p[o + 1] = sy;
      p[o + 2] = (ndcZ + 1) * 0.5;
      if (sx < minX) minX = sx;
      if (sx > maxX) maxX = sx;
      if (sy < minY) minY = sy;
      if (sy > maxY) maxY = sy;
    }
    const rectWidth = Math.min(width, maxX) - Math.max(0, minX);
    const rectHeight = Math.min(height, maxY) - Math.max(0, minY);
    this.lastBoxRect = rectWidth > 0 && rectHeight > 0 ? rectWidth * rectHeight : 0;
    return true;
  }

  /**
   * @deprecated Use rasterizeOccluderOBB for better accuracy with rotated objects
   * Writes no coverage bits: with coverageSubSamples > 1 anything behind this fill stays Visible
   * (safe but useless). Do not FULL-fill the rect: it is larger than the box's silhouette and would
   * deny rescues over background.
   */
  rasterizeOccluder(bounds: THREE.Box3): void {
    const corners = [
      new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
      new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.max.z),
      new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.min.z),
      new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.max.z),
      new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.min.z),
      new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.max.z),
      new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.min.z),
      new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.max.z),
    ];
    this.rasterizeOccluderOBB(corners);
  }

  /**
   * Test if an object is occluded using world-space OBB corners.
   * @param worldCorners - 8 corners of the OBB in world space
   * @returns true if fully occluded, false if potentially visible
   */
  isOccludedOBB(worldCorners: THREE.Vector3[]): boolean {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minDepth = Infinity;
    let anyVisible = false;

    for (const corner of worldCorners) {
      // Use Vector4 for explicit w check (Vector3.applyMatrix4 does implicit
      // perspective divide, producing incorrect results for behind-camera points)
      const v = new THREE.Vector4(corner.x, corner.y, corner.z, 1);
      v.applyMatrix4(this.viewProjectionMatrix);

      // If any corner is behind the camera, the OBB straddles the near plane —
      // treat as not occluded (camera is very close to or inside this object)
      if (v.w <= 0) {
        return false;
      }

      // Manual perspective divide
      const ndcX = v.x / v.w;
      const ndcY = v.y / v.w;
      const ndcZ = v.z / v.w;

      // If corner is in front of near plane, potentially visible
      if (ndcZ >= -1 && ndcZ <= 1) {
        anyVisible = true;

        const screenX = (ndcX + 1) * 0.5 * this.width;
        const screenY = (1 - ndcY) * 0.5 * this.height;
        const depth = (ndcZ + 1) * 0.5;

        minX = Math.min(minX, screenX);
        maxX = Math.max(maxX, screenX);
        minY = Math.min(minY, screenY);
        maxY = Math.max(maxY, screenY);
        minDepth = Math.min(minDepth, depth);
      }
    }

    // If entirely off-screen or behind camera, not occluded (render it)
    if (!anyVisible || minX > this.width || maxX < 0 || minY > this.height || maxY < 0) {
      return false;
    }

    // Clamp to buffer bounds
    minX = Math.max(0, Math.floor(minX));
    maxX = Math.min(this.width - 1, Math.ceil(maxX));
    minY = Math.max(0, Math.floor(minY));
    maxY = Math.min(this.height - 1, Math.ceil(maxY));

    // Check if object's closest point (minDepth) is visible at any pixel
    // Object is visible if its front face is in front of what's in the depth buffer by more
    // than the occludee depth margin (strict, a tie is not visible), or the pixel is empty
    // (was: "closer than or equal to what's in the depth buffer")
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        // was: if (minDepth <= this.getDepth(x, y)) {
        const stored = this.getDepth(x, y);
        if (stored >= 1 || minDepth < stored * this.marginScale - this.marginBias) {
          return false; // Object's front face is visible at this pixel
        }
      }
    }

    return true; // Object's front face is entirely behind depth buffer
  }

  /** @deprecated Use isOccludedOBB for better accuracy with rotated objects */
  isOccluded(bounds: THREE.Box3): boolean {
    const corners = [
      new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
      new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.max.z),
      new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.min.z),
      new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.max.z),
      new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.min.z),
      new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.max.z),
      new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.min.z),
      new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.max.z),
    ];
    return this.isOccludedOBB(corners);
  }
}
